import React, { useRef, useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { formatValue, formatPercentage } from "../Utils";



const CustomizedLabel = (props) => {
	const { x, y, width, value, format, totalValue, labelDisplay, label, labelColor, labelWeight = 400, labelPosition = "left" } = props;
	const fillColor = labelColor ?? "hsl(var(--chart-axis))";
	const textAnchorMap = { left: "start", right: "end", center: "middle" };
	const textAnchor = textAnchorMap[labelPosition] ?? "start";
	const posX = labelPosition === "left" ? (x ?? 5) + 5 : labelPosition === "right" ? (x ?? 0) + (width ?? 0) - 5 : (x ?? 0) + (width ?? 0) / 2;
	const textProps = { x: posX, y: y - 4, dy: 18, dx: labelPosition === "right" ? -10 : labelPosition === "center" ? 0 : 10, fontSize: 12, fontWeight: labelWeight, fill: fillColor, textAnchor };
	const suffix = label != null && label !== "" ? ` ${label}` : "";

	if (labelDisplay === "percentage" && totalValue != null && Number(totalValue) !== 0) {
		const pct = Math.round((Number(value) / Number(totalValue)) * 100);
		return <text {...textProps}>{pct}%{suffix}</text>;
	}
	if (format === "currency") {
		return <text {...textProps}>{formatValue(value, "currency", false)}{suffix}</text>;
	}
	if (format === "percentage") {
		return <text {...textProps}>{formatPercentage(value, false)}{suffix}</text>;
	}
	return <text {...textProps}>{value}{suffix}</text>;
};

/** Custom bar shape: draws a background track (same height as bar) then the bar on top. Both have rounded ends. */
const BarWithTrackShape = (props) => {
	const { x, y, width, height, fill, totalValue, backgroundColor, contentWidth } = props;
	const trackWidthPx =
		totalValue != null && Number(props.value) > 0
			? (width * totalValue) / Number(props.value)
			: contentWidth != null
			? contentWidth
			: width;
	const rx = height / 2;
	const ry = height / 2;
	return (
		<g>
			{backgroundColor != null && (
				<rect
					x={x}
					y={y}
					width={trackWidthPx}
					height={height}
					rx={rx}
					ry={ry}
					fill={backgroundColor}
					fillOpacity={1}
				/>
			)}
			<rect x={x} y={y} width={width} height={height} rx={rx} ry={ry} fill={fill} />
		</g>
	);
};

export const SparkBar = (props) => {
	const { data, format,  totalValue, barColor,  backgroundColor, labelDisplay = "value", width = 150, label, labelColor, labelWeight = 400, labelPosition = "left", fullWidth = false } = props;
    const dataFound = true;
    const containerRef = useRef(null);
    const [measuredWidth, setMeasuredWidth] = useState(width);

    useEffect(() => {
        if (!fullWidth || !containerRef.current) return;
        const el = containerRef.current;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) setMeasuredWidth(e.contentRect.width);
        });
        ro.observe(el);
        setMeasuredWidth(el.getBoundingClientRect().width);
        return () => ro.disconnect();
    }, [fullWidth]);

    const barData = [{ value: data }];
    const contentWidth = fullWidth ? measuredWidth - 10 : width - 10;

    const chart = (
        <BarChart
            layout={"vertical"}
            width={fullWidth ? undefined : width}
            height={24}
            margin={{ top: 0, left: 5, bottom: 0, right: 5 }}
            className={fullWidth ? "" : "mr-6"}
            data={barData}
        >
            <Bar
                dataKey="value"
                fill={barColor}
                isAnimationActive={false}
                shape={(shapeProps) => (
                    <BarWithTrackShape
                        {...shapeProps}
                        totalValue={totalValue}
                        backgroundColor={backgroundColor}
                        contentWidth={contentWidth}
                    />
                )}
                label={<CustomizedLabel format={format} totalValue={totalValue} labelDisplay={labelDisplay} label={label} labelColor={labelColor} labelWeight={labelWeight} labelPosition={labelPosition} />}
            />
            <XAxis type="number" hide={true} domain={[0, totalValue]} />
            <YAxis dataKey="name" type="category" hide={true} tickLine={false} />
        </BarChart>
    );

	return (
		<div ref={fullWidth ? containerRef : undefined} className={fullWidth ? "w-full min-w-0" : ""}>
            {dataFound ? (
                <div className="flex flex-row">
                    {fullWidth ? (
                        <ResponsiveContainer width="100%" height={24}>
                            {chart}
                        </ResponsiveContainer>
                    ) : (
                        chart
                    )}
                </div>
            ) : (
                <div></div>
            )}
		</div>
	);
};
